import { createSlice, current } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import {
  getFilters,
  getFormColumns,
  getFormData,
  getFormStatus,
  getAnalysis,
  getAIBuilder,
  getQuotaData,
  getDownloadData,
  getIndustryList,
  getTaxonomyById,
  getTaxonomyList,
  getTranscriptStatus,
  getAllTranscription,
  getComparators,
  getThemeList,
  getVideoList,
  getOverallColumns,
  getOverallData,
  getOverallStatus,
  getSavedFilterList,
  getShowReelsList,
  getShowReelsUploadImages,
  getReelElements,
  getDownloadFilesList,
  getUploadList,
  getSingleExcel,
  getContentAnalysisColumn,
  getContentAnalysisData,
  getFilterComparison,
  getOperationStatus,
  getShowReelsElements,
} from '../asyncActions/dashboardActions';
import { industryType, categoryType } from './Types';

type sortObjectType = {
  dataIndex: string;
  desc: boolean;
};

type transcriptionStatusObj = {
  approved?: number;
  notApproved?: number;
  notUpdated?: number;
  proofRead?: number;
  total?: number;
  updated?: number;
  videoTime?: number;
};

export type transcriptionDataObj = {
  isAnalysisApproved: boolean;
  isUpdate: boolean;
  transcriberName: string;
  transcript: string;
  url: string;
  userId: number;
  videoId: number;
  editable?: boolean;
  questionOrderId: number;
  userName?: string;
  userEmail?: string;
  answerType?: string;
};

type FilterObjectProp = {
  filterType?: string;
  filterId?: string;
  value?: string;
};

export type OperationStatusObj = {
  id?: number;
  batchId: number;
  batchTitle: string;
  questionId: number;
  operationName: string;
  isNotified: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  completedValue: number;
  operationGroupId: number;
  researchType:
    | 'survey'
    | 'video_diary'
    | 'long_format'
    | 'product_testing'
    | 'excel';
  message: string;
  index?: string[];
  isOverall?: boolean;
};

type OperationStatusDrawerObj = {
  operations: OperationStatusObj[];
  havePendingOperations: boolean;
  notificationCount: number;
  isDrawerOpen: boolean;
};

interface dashboardData {
  loading: boolean;
  formStatus?: object;
  overallStatus?: Array<object>;
  formColumns?: Array<object>;
  overallColumns?: Array<object>;
  formData: Array<object>;
  overallData: Array<object>;
  formDataTotalCount: number;
  overAllDataTotalCount: number;
  filterDataValue: string;
  filters?: object;
  selectedFilters?: object;
  selectedComparisonQns: Array<object>;
  formSortObject?: sortObjectType;
  overallDataSortObject?: sortObjectType;
  analysisData?: any;
  analysisDataExcelValue?: any;
  valueNps?: object;
  matrixData?: object;
  AIBuilderData?: object;
  quotaData?: object;
  downloadData?: object;
  newdownloadData?: object;
  selectedTaxonomyId?: number | null;
  selectedCategoryId?: number | null;
  selectedIndustryId?: number | null;
  industryList?: industryType[];
  categoryList?: categoryType[];
  taxonomyList: object;
  taxonomyDropdownList?: industryType[];
  transcriptionStatus?: transcriptionStatusObj;
  allTranscriptionData?: transcriptionDataObj[];
  allTranscriptionDataTotalCount: number;
  formDataLoading: boolean;
  overAllDataLoading: boolean;
  comparator: object;
  total: string;
  themeList: Array<object>;
  videoList: Array<object>;
  videoListCount: number;
  getAnalysisReqObj: object;
  isSaving: boolean | null;
  longFormatUploadingFiles: Array<object>;
  savedFilterList: Array<object>;
  selectedFilterId: number | null;
  showReelsList: Array<object>;
  showReelsUploadedImages: Array<object>;
  showReelsElements: Array<object>;
  currentReelLayers: Array<object>;
  updateTimelineTextData: object;
  updateTimelineImageData: object;
  updateTimelineElementData: object;
  downloadFilesList: Array<object>;
  uploadList: Array<object>;
  excelData: any;
  contentAnalysisColumns: any;
  contentAnalysisData: any;
  quadrantChartData: any;
  dataTableCurrentlyUploadingFiles: Array<any>;
  longFormatUploadingFilesBatchId: number;
  questionBuilderIsMediaUploading: boolean;
  filterCompare: {
    selectedFilters: Array<number>;
    comparisonData: Array<{
      filterId: number;
      filterTitle: string;
      dashboardData: {
        sentimentScore: string;
        sentimentColor: string;
        total: string;
        sentiment: Array<{
          sentiment: string;
          color: string;
          users: number;
          count: number;
        }>;
      };
      themeData: Array<{
        theme: string;
        sentiment: string;
        color: string;
        users: number;
        usersPercentage: number;
        sentimentObj: Array<{
          sentiment: string;
          sentimentValue: number;
          color: string;
          count: number;
          users: number;
        }>;
      }>;
    }>;
  };
  reelPlayerMediaVideoElements: object;
  operationStatusDrawer: OperationStatusDrawerObj;
  reelExportData: {
    list: Array<any>;
    downloadInProgress: boolean;
  };
  reelPlayerTextParentId: {
    parentId: number;
    startTime: number;
    endTime: number;
  } | null;
}

const initialState: dashboardData = {
  loading: false,
  filters: {},
  filterCompare: {
    selectedFilters: [],
    comparisonData: [],
  },
  selectedFilters: {},
  selectedComparisonQns: [{}],
  formStatus: {},
  overallStatus: [],
  formColumns: [],
  overallColumns: [],
  formData: [],
  overallData: [],
  formDataTotalCount: 0,
  overAllDataTotalCount: 0,
  filterDataValue: '',
  formSortObject: { dataIndex: 'id', desc: true },
  overallDataSortObject: { dataIndex: 'id', desc: true },
  analysisData: [],
  analysisDataExcelValue: [],
  matrixData: {},
  AIBuilderData: {},
  quotaData: {},
  downloadData: {},
  newdownloadData: {},
  industryList: [],
  categoryList: [],
  taxonomyList: {},
  taxonomyDropdownList: [],
  transcriptionStatus: {},
  allTranscriptionData: [],
  allTranscriptionDataTotalCount: 0,
  valueNps: {},
  formDataLoading: true,
  overAllDataLoading: false,
  comparator: {},
  total: '',
  themeList: [],
  videoList: [],
  videoListCount: 0,
  getAnalysisReqObj: {},
  isSaving: null,
  isExcelUploading: false,
  longFormatUploadingFiles: [],
  savedFilterList: [],
  selectedFilterId: null,
  showReelsList: [],
  showReelsUploadedImages: [],
  currentReelLayers: [],
  updateTimelineTextData: {},
  updateTimelineImageData: {},
  updateTimelineElementData: {},
  downloadFilesList: [],
  uploadList: [],
  excelData: [],
  contentAnalysisColumns: [],
  contentAnalysisData: [],
  quadrantChartData: null,
  dataTableCurrentlyUploadingFiles: [],
  longFormatUploadingFilesBatchId: -1,
  questionBuilderIsMediaUploading: false,
  reelPlayerMediaVideoElements: {},
  operationStatusDrawer: {
    operations: [],
    havePendingOperations: false,
    notificationCount: 0,
    isDrawerOpen: false,
  },
  reelExportData: {
    list: [],
    downloadInProgress: false,
  },
  reelPlayerTextParentId: null,
};

export const DashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setReelPlayerTextParentId: (state, action) => {
      state.reelPlayerTextParentId = action.payload;
    },
    setReelExportData: (state, action) => {
      const sortedList = [...(action.payload?.value ?? [])].sort(
        (a, b) => Number(b.createdAt) - Number(a.createdAt),
      );

      state.reelExportData = {
        list: sortedList,
        downloadInProgress:
          action.payload?.downloadStatus === 'in-progress' ?? false,
      };
    },

    setReelPlayerVideoElement: (state, action) => {
      let { url, element } = action.payload;
      state.reelPlayerMediaVideoElements = {
        ...state.reelPlayerMediaVideoElements,
        [url]: element,
      };
    },
    clearReelPlayerVideoElement: (state) => {
      state.reelPlayerMediaVideoElements = {};
    },
    addPartialData: (state, action) => {
      if (!action.payload.index) state.formData = [...action.payload.data];
      else state.formData = [...state.formData, ...action.payload.data]; // Append data
      state.formDataTotalCount = action.payload.totalCount;
    },
    setFormData: (state, action) => {
      state.formData = [...action.payload.data]; // Append data
      state.formDataTotalCount = action.payload.totalCount;
      state.formDataLoading = false;
    },
    emptyFormData: (state) => {
      state.formColumns = initialState.formColumns;
      state.formData = initialState.formData;
      state.formDataTotalCount = initialState.formDataTotalCount;
      state.formSortObject = initialState.formSortObject;
      state.overallDataSortObject = initialState.overallDataSortObject;
      state.formDataLoading = true;
    },
    setDataTableCurrentlyUploadingFiles: (state, action) => {
      state.dataTableCurrentlyUploadingFiles = action.payload;
    },
    setLongFormatUploadingFilesBatchId: (state, action) => {
      state.longFormatUploadingFilesBatchId = action.payload;
    },
    setLongFormatFiles: (state, action) => {
      state.longFormatUploadingFiles = action.payload;
    },
    setIsQuestionBuilderMediaSaving: (state, action) => {
      state.questionBuilderIsMediaUploading = action.payload;
    },
    setIsSaving: (state, action) => {
      state.isSaving = action.payload;
    },
    setIsExcelUploading: (state, action) => {
      state.isExcelUploading = action.payload;
    },
    setFormSortObject: (state, action) => {
      state.formSortObject = action.payload;
    },
    setOverallDataSortObject: (state, action) => {
      state.overallDataSortObject = action.payload;
    },
    setDemoGraphyFilters: (state, action) => {
      state.selectedFilters = action.payload;
    },
    setComparisonQns: (state, action) => {
      state.selectedComparisonQns = action.payload;
    },
    setSelectedFilterId: (state, action) => {
      state.selectedFilterId = action.payload;
    },
    clearCompareFilters: (state) => {
      state.filterCompare.selectedFilters = [];
      state.filterCompare.comparisonData = [];
    },
    setTaxonomyDropDown: (state, action) => {
      const [selectedIndustryId, selectedCategoryId] = action.payload;
      state.selectedIndustryId = selectedIndustryId;
      state.selectedCategoryId = selectedCategoryId;
    },
    deleteFilter: (state, action) => {
      const { key, value } = action.payload;
      let currentFilters = state.selectedFilters;
      let index = currentFilters[key].indexOf(value);
      if (index !== -1) {
        currentFilters[key].splice(index, 1);
      }
      state.selectedFilters = currentFilters;
    },
    deleteCompareQuestion: (state, action) => {
      const { value, id, header } = action.payload;

      let currentSelectedQuestions = current(state).selectedComparisonQns;
      let questionIndex = currentSelectedQuestions.findIndex(
        (question: any) => question.questionId === id,
      );
      let currentQuestion = currentSelectedQuestions[questionIndex];
      let answerType = currentQuestion.answerType;
      let answer = cloneDeep(currentQuestion.answer);
      switch (answerType) {
        case 'matrix':
        case 'video':
        case 'AVT':
        case 'AVT-Text':
        case 'imageCapture':
        case 'audio': {
          answer[header] = answer[header].filter((item: any) => item !== value);
          let newQuestionObject = {
            questionId: id,
            answerType: answerType,
            answer: answer,
          };
          currentSelectedQuestions = currentSelectedQuestions.slice();
          currentSelectedQuestions[questionIndex] = newQuestionObject;
          break;
        }
        default: {
          answer = answer.filter((item: any) => item !== value);
          let newQuestionObject = {
            questionId: id,
            answerType: answerType,
            answer: answer,
          };
          currentSelectedQuestions = currentSelectedQuestions.slice();
          currentSelectedQuestions[questionIndex] = newQuestionObject;
          break;
        }
      }
      state.selectedComparisonQns = currentSelectedQuestions;
    },
    clearFIlters: (state) => {
      state.selectedFilters = initialState.selectedFilters;
      state.selectedComparisonQns = initialState.selectedComparisonQns;
    },
    setAnalysisReqObjVideo: (state, action) => {
      state.getAnalysisReqObj = action.payload;
    },
    setUpdateTimlineTextData: (state, action) => {
      state.updateTimelineTextData = action.payload;
    },
    setUpdateTimlineImageData: (state, action) => {
      state.updateTimelineImageData = action.payload;
    },
    setUpdateTimlineElementData: (state, action) => {
      state.updateTimelineElementData = action.payload;
    },
    setCurrentReelLayers: (state, action) => {
      state.currentReelLayers = action.payload;
    },
    updateReelLayerProperty: (state, action) => {
      const { reelProjectInfoId, updates } = action.payload;
      state.currentReelLayers = state.currentReelLayers.map((layer: any) =>
        layer.reelProjectInfoId === reelProjectInfoId
          ? { ...layer, ...updates }
          : layer
      );
    },
    addFilter: (state, action) => {
      state.filterCompare.selectedFilters = action.payload;
    },
    setOperationStatusDrawerOpen: (state, action) => {
      state.operationStatusDrawer.isDrawerOpen = action.payload;
    },
    setOperationStatusNotificationCount: (state, action) => {
      state.operationStatusDrawer.notificationCount = action.payload;
    },
    clearOperationStatus: (state) => {
      state.operationStatusDrawer.operations = [];
      state.operationStatusDrawer.havePendingOperations = false;
      state.operationStatusDrawer.notificationCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getContentAnalysisData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getContentAnalysisData.fulfilled, (state, action) => {
        state.contentAnalysisData = action.payload.data.value;
        state.loading = false;
      })
      .addCase(getContentAnalysisData.rejected, (state, payload) => {
        state.loading = false;
      })
      .addCase(getContentAnalysisColumn.pending, (state) => {
        state.loading = true;
      })
      .addCase(getContentAnalysisColumn.fulfilled, (state, action) => {
        state.contentAnalysisColumns = action.payload.data.value;
        state.loading = false;
      })
      .addCase(getContentAnalysisColumn.rejected, (state, payload) => {
        state.loading = false;
      })
      .addCase(getSingleExcel.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSingleExcel.fulfilled, (state, action) => {
        state.excelData = action.payload.data.value;
        state.loading = false;
      })
      .addCase(getSingleExcel.rejected, (state, payload) => {
        state.loading = false;
      })
      .addCase(getUploadList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUploadList.fulfilled, (state, action) => {
        state.uploadList = action.payload.data.value;
        state.loading = false;
      })
      .addCase(getUploadList.rejected, (state, payload) => {
        state.loading = false;
      })
      .addCase(getFilters.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFilters.fulfilled, (state, action) => {
        state.filters = action.payload.data.value;
        state.loading = false;
      })
      .addCase(getFilters.rejected, (state, payload) => {
        state.loading = false;
      })
      .addCase(getComparators.pending, (state) => {
        state.loading = true;
      })
      .addCase(getComparators.fulfilled, (state, action) => {
        state.comparator = action.payload.data.value;
        state.loading = false;
      })
      .addCase(getComparators.rejected, (state, payload) => {
        state.loading = false;
      })

      .addCase(getAnalysis.pending, (state) => {
        state.analysisData = [];
        state.analysisDataExcelValue = [];
        state.matrixData = {};
        state.valueNps = {};
        state.loading = true;
        state.total = '';
      })
      .addCase(getAnalysis.fulfilled, (state, action) => {
        const data = action.payload?.data;
        if (!data) {
          state.loading = false;
          return;
        }
        if (data.answerType === 'matrix') {
          state.matrixData = data.value;
          state.analysisDataExcelValue = data.excelValue || [];
          state.total = data.total || '';
        } else if (data.answerType === 'quadrantChart') {
          state.quadrantChartData = {
            excelValue: data.excelValue ?? [],
            questionObject: data.questionObject ?? {},
          };
          state.total = data.total || '';
          state.analysisDataExcelValue = data.excelValue || [];
        } else {
          state.analysisData = data.value;
          state.analysisDataExcelValue = data.value || [];
          state.valueNps = data.valueNps || {};
          state.total = data.total || '';
        }
        state.loading = false;
      })
      .addCase(getAnalysis.rejected, (state, payload) => {
        state.loading = false;
      })
      .addCase(getFormStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFormStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.formStatus = action.payload.data.value;
      })
      .addCase(getFormStatus.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getOverallStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOverallStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.overallStatus = action?.payload?.data?.value ?? [];
      })
      .addCase(getOverallStatus.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getFormColumns.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFormColumns.fulfilled, (state, action) => {
        state.loading = false;
        state.formColumns = action.payload.data.value;
      })
      .addCase(getFormColumns.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getOverallColumns.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOverallColumns.fulfilled, (state, action) => {
        state.loading = false;
        state.overallColumns = action?.payload?.data?.value ?? [];
      })
      .addCase(getOverallColumns.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getOverallData.pending, (state) => {
        state.overAllDataLoading = true;
      })
      .addCase(getOverallData.fulfilled, (state, action) => {
        state.overallData = action?.payload?.data?.value ?? [];
        state.overAllDataTotalCount = action?.payload?.data?.totalCount ?? 0;
        state.overAllDataLoading = false;
      })
      .addCase(getOverallData.rejected, (state, payload) => {
        state.overAllDataLoading = false;
        state = initialState;
      })
      .addCase(getFormData.pending, (state) => {
        state.formDataLoading = true;
      })
      .addCase(getFormData.fulfilled, (state, action) => {
        // state.formData = action.payload?.data.data;
        // state.formDataTotalCount = action.payload?.data.totalCount;
        state.formDataLoading = false;
      })
      .addCase(getFormData.rejected, (state, payload) => {
        state.formDataLoading = false;
        state = initialState;
      })
      .addCase(getAIBuilder.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAIBuilder.fulfilled, (state, action) => {
        state.loading = false;
        state.AIBuilderData = action.payload.data.value;
      })
      .addCase(getAIBuilder.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getQuotaData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getQuotaData.fulfilled, (state, action) => {
        state.loading = false;
        state.quotaData = action.payload.data;
      })
      .addCase(getQuotaData.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getDownloadData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDownloadData.fulfilled, (state, action) => {
        state.loading = false;
        state.downloadData = action.payload.data;
      })
      .addCase(getDownloadData.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getIndustryList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getIndustryList.fulfilled, (state, action) => {
        state.loading = false;
        state.industryList = action.payload.data.industry;
        state.categoryList = action.payload.data.category;
      })
      .addCase(getIndustryList.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getTaxonomyById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTaxonomyById.fulfilled, (state, action) => {
        state.loading = false;
        state.taxonomyList = action.payload.data.value;
      })
      .addCase(getTaxonomyById.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getTaxonomyList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTaxonomyList.fulfilled, (state, action) => {
        state.loading = false;
        state.taxonomyDropdownList = action.payload.data.value;
      })
      .addCase(getTaxonomyList.rejected, (state, payload) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getTranscriptStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTranscriptStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.transcriptionStatus = action.payload.data.transcriptStatus;
      })
      .addCase(getTranscriptStatus.rejected, (state) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getAllTranscription.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTranscription.fulfilled, (state, action) => {
        state.loading = false;
        state.allTranscriptionData = action.payload.data.value;
        state.allTranscriptionDataTotalCount = action.payload.data.count;
      })
      .addCase(getAllTranscription.rejected, (state) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getThemeList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getThemeList.fulfilled, (state, action) => {
        state.loading = false;
        state.themeList = action.payload.data.value;
      })
      .addCase(getThemeList.rejected, (state) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getVideoList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVideoList.fulfilled, (state, action) => {
        state.loading = false;
        state.videoListCount = action.payload.data.count;
        state.videoList = action.payload.data.value;
      })
      .addCase(getVideoList.rejected, (state) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getSavedFilterList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSavedFilterList.fulfilled, (state, action) => {
        state.loading = false;
        state.savedFilterList = action.payload.data.value;
      })
      .addCase(getSavedFilterList.rejected, (state) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getShowReelsList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getShowReelsList.fulfilled, (state, action) => {
        state.loading = false;
        state.showReelsList = action.payload.data.value;
      })
      .addCase(getShowReelsList.rejected, (state) => {
        state.loading = false;
        state.showReelsList = [];
      })
      .addCase(getShowReelsUploadImages.pending, (state) => {
        state.loading = true;
      })
      .addCase(getShowReelsUploadImages.fulfilled, (state, action) => {
        state.loading = false;
        state.showReelsUploadedImages = action.payload.data.value;
      })
      .addCase(getShowReelsUploadImages.rejected, (state) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getShowReelsElements.pending, (state) => {
        state.loading = true;
      })
      .addCase(getShowReelsElements.fulfilled, (state, action) => {
        state.loading = false;
        state.showReelsElements = action.payload.data.value;
      })
      .addCase(getShowReelsElements.rejected, (state) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getReelElements.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReelElements.fulfilled, (state, action) => {
        state.loading = false;
        const incomingLayers = [
          ...action.payload.data.value?.mainTimeLine,
          ...action.payload.data.value?.textTimeLine,
          ...action.payload.data.value?.elementsTimeLine,
        ];

        // Merge instead of replace: keep old object references for unchanged layers
        // This prevents unnecessary re-renders and worker re-computation downstream
        const prevLayers = state.currentReelLayers as any[];
        if (prevLayers.length === 0) {
          state.currentReelLayers = incomingLayers;
        } else {
          // Build lookup of existing layers by ID for O(1) matching
          const prevMap = new Map<number, any>();
          for (const layer of prevLayers) {
            if (layer.reelProjectInfoId != null) {
              prevMap.set(layer.reelProjectInfoId, layer);
            }
          }

          let hasAnyChange = false;
          const merged = incomingLayers.map((incoming: any) => {
            const existing = prevMap.get(incoming.reelProjectInfoId);
            if (!existing) {
              hasAnyChange = true;
              return incoming;
            }

            // Shallow compare all keys — if identical, keep the old reference
            const allKeys = new Set([
              ...Object.keys(existing),
              ...Object.keys(incoming),
            ]);
            let layerChanged = false;
            for (const key of allKeys) {
              if (existing[key] !== incoming[key]) {
                layerChanged = true;
                break;
              }
            }

            if (layerChanged) {
              hasAnyChange = true;
              return incoming;
            }
            return existing; // Keep old reference — no re-render for this layer
          });

          // Only replace array if something actually changed or length differs
          if (hasAnyChange || merged.length !== prevLayers.length) {
            state.currentReelLayers = merged;
          }
          // else: keep existing array reference entirely (no downstream triggers)
        }
      })
      .addCase(getReelElements.rejected, (state) => {
        state.loading = false;
        state.currentReelLayers = [];
      })
      .addCase(getDownloadFilesList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDownloadFilesList.fulfilled, (state, action) => {
        state.loading = false;
        state.downloadFilesList = action.payload.data.value;
      })
      .addCase(getDownloadFilesList.rejected, (state) => {
        state.loading = false;
        state = initialState;
      })
      .addCase(getFilterComparison.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFilterComparison.fulfilled, (state, action) => {
        state.filterCompare.comparisonData = action.payload;
        state.loading = false;
      })
      .addCase(getFilterComparison.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getOperationStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOperationStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.operationStatusDrawer.operations =
          action.payload.operations ?? [];
        state.operationStatusDrawer.havePendingOperations =
          action.payload.havePendingOperations;
        state.operationStatusDrawer.notificationCount =
          action.payload.operations?.filter(
            (operation: OperationStatusObj) =>
              !operation.isNotified &&
              ['COMPLETED', 'FAILED', 'DONE'].includes(operation.status),
          )?.length ?? 0;
      })
      .addCase(getOperationStatus.rejected, (state) => {
        state.loading = false;
        state.operationStatusDrawer.operations = [];
        state.operationStatusDrawer.havePendingOperations = false;
        state.operationStatusDrawer.notificationCount = 0;
      });
  },
});

export const { actions: dashboardActions } = DashboardSlice;
export default DashboardSlice.reducer;
